import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useApolloClient } from "@apollo/client/react";
import { useAuth } from "./AuthContext";
import { GET_DOCUMENT_QUERY } from "../graphql/queries/files";
import type { DocumentStatusState } from "../hooks/useDocumentProcessingStream";
import {
  applyStatusEvent,
  buildDefaultContextLabel,
  clearJobsStorage,
  countActiveJobs,
  countUnreadJobs,
  countBadgeJobs,
  isJobActive,
  isJobCompleted,
  isJobFailed,
  isJobInReview,
  isJobStuckInQueue,
  jobToStatusState,
  jobsToStatusMap,
  loadJobsFromStorage,
  logSseDebug,
  pruneJobs,
  removeJobsByDocumentIds,
  resolveJobProgress,
  saveJobsToStorage,
  shouldApplyBootstrap,
  shouldSyncJobFromApi,
  normalizeStreamStatus,
  type DocumentExtractionJob,
  type DocumentUploadContext,
  type StatusEventPayload,
} from "../utils/documentExtractionJobs";
import { dispatchExtractionReviewReady, dispatchExtractionEtlSync, onDocumentDeleted } from "../utils/documentExtractionEvents";
import { jobReadyForReview } from "../utils/documentExtractionReviewOpen";
import {
  getTenantStatusStreamUrl,
  parseDocumentStatusPayload,
  parseEventBlocks,
  parseSseEvent,
} from "../utils/documentExtractionSse";
import { isTerminalEtlStatus, isEtlComplete, normalizeEtlStatus } from "../utils/documentExtractionStatus";

export type { DocumentUploadContext, DocumentExtractionJob };

interface RegisterDocInput {
  id: string;
  name: string;
  mime_type?: string;
  progress?: number | null;
  status?: string | null;
  stage?: string | null;
  /** Document created_at — used for stuck-in-queue detection in bell/cards. */
  queuedSince?: string | null;
}

interface DocumentExtractionContextValue {
  jobs: Record<string, DocumentExtractionJob>;
  statusMap: Record<string, DocumentStatusState>;
  isConnected: boolean;
  isBootstrapping: boolean;
  activeCount: number;
  unreadCount: number;
  badgeCount: number;
  stuckCheckTick: number;
  registerJobs: (docs: RegisterDocInput[], context: DocumentUploadContext) => void;
  /** Correct stale bell jobs from search/API without creating new entries. */
  syncJobsFromApi: (docs: RegisterDocInput[]) => void;
  getJob: (documentId: string) => DocumentExtractionJob | undefined;
  dismissJob: (documentId: string) => void;
  markAllRead: () => void;
  markJobRead: (documentId: string) => void;
  markJobSubmitted: (documentId: string) => void;
  reviewReadyJob: DocumentExtractionJob | null;
  consumeReviewReadyJob: () => void;
}

const DocumentExtractionContext = createContext<DocumentExtractionContextValue | undefined>(
  undefined
);

function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("accessToken");
}

function getActiveJobIds(jobs: Record<string, DocumentExtractionJob>): string[] {
  return Object.values(jobs)
    .filter((j) => !isTerminalEtlStatus(j.status))
    .map((j) => j.documentId);
}

export function DocumentExtractionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const apolloClient = useApolloClient();
  const [jobs, setJobs] = useState<Record<string, DocumentExtractionJob>>({});
  const [reviewReadyJob, setReviewReadyJob] = useState<DocumentExtractionJob | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [stuckCheckTick, setStuckCheckTick] = useState(0);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const autoOpenedReviewRef = useRef(new Set<string>());

  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const persistTimerRef = useRef<number | null>(null);
  const bootstrapTimerRef = useRef<number | null>(null);
  const sseReconnectTimerRef = useRef<number | null>(null);
  const activeSseIdsRef = useRef<string>("");
  const sessionInitializedRef = useRef(false);
  const connectSseRef = useRef<(() => Promise<void>) | null>(null);

  const isAuthenticated = Boolean(user && getAccessToken());

  const flushJobsToStorage = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    const current = jobsRef.current;
    if (countActiveJobs(current) > 0) {
      saveJobsToStorage(pruneJobs(current));
    }
  }, []);

  const persistJobs = useCallback((next: Record<string, DocumentExtractionJob>) => {
    const pruned = pruneJobs(next);
    if (countActiveJobs(pruned) === 0) {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      clearJobsStorage();
      return pruned;
    }
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = window.setTimeout(() => {
      saveJobsToStorage(pruned);
    }, 300);
    return pruned;
  }, []);

  const clearAllJobs = useCallback(() => {
    setJobs({});
    clearJobsStorage();
  }, []);

  const disconnectSse = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const handleStatusEvent = useCallback(
    (payload: StatusEventPayload) => {
      const tracked = jobsRef.current[payload.documentId];
      if (!tracked) {
        logSseDebug("ignored (untracked)", payload);
        return;
      }

      const before = { ...tracked };
      const next = applyStatusEvent(tracked, payload);
      logSseDebug("applied", {
        documentId: payload.documentId,
        status: next.status,
        progress: next.progress,
        before: { status: before.status, progress: before.progress },
      });

      if (
        jobReadyForReview(before, next) &&
        !autoOpenedReviewRef.current.has(next.documentId)
      ) {
        autoOpenedReviewRef.current.add(next.documentId);
        setReviewReadyJob(next);
        dispatchExtractionReviewReady(next);
      }

      setJobs((prev) => {
        const updated = persistJobs({ ...prev, [payload.documentId]: next });
        return updated;
      });

      const nextN = normalizeEtlStatus(next.status);
      if (nextN === "in_review" || (nextN === "processing" && (next.progress ?? 0) >= 100)) {
        void apolloClient
          .query<{
            document?: {
              id: string;
              etl?: {
                status?: string | null;
                progress?: number | null;
                stage?: string | null;
                error?: string | null;
                processed_at?: string | null;
              } | null;
            } | null;
          }>({
            query: GET_DOCUMENT_QUERY,
            variables: { id: payload.documentId },
            fetchPolicy: "network-only",
          })
          .then((result) => {
            const etl = result.data?.document?.etl;
            if (!etl) return;
            dispatchExtractionEtlSync({
              documentId: payload.documentId,
              etl,
            });
            setJobs((prev) => {
              const job = prev[payload.documentId];
              if (!job || isEtlComplete(job.status)) return prev;
              const apiStatus = etl.status
                ? normalizeStreamStatus(etl.status)
                : job.status;
              if (normalizeEtlStatus(apiStatus) !== "in_review") return prev;
              const synced = applyStatusEvent(
                job,
                {
                  documentId: payload.documentId,
                  status: apiStatus,
                  progress: etl.progress ?? 100,
                  stage: etl.stage ?? null,
                  error: etl.error ?? null,
                  processed_at: etl.processed_at ?? new Date().toISOString(),
                },
                { fromBootstrap: true }
              );
              return persistJobs({ ...prev, [payload.documentId]: synced });
            });
          })
          .catch(() => {
            // non-blocking — job state from SSE is still applied
          });
      }
    },
    [apolloClient, persistJobs]
  );

  const connectSse = useCallback(async () => {
    if (!isAuthenticated) return;

    disconnectSse();
    const controller = new AbortController();
    abortRef.current = controller;

    const activeIds = getActiveJobIds(jobsRef.current);
    const streamUrl = getTenantStatusStreamUrl(activeIds.length > 0 ? activeIds : undefined);
    activeSseIdsRef.current = activeIds.slice().sort().join(",");

    try {
      const token = getAccessToken();
      const response = await fetch(streamUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Status stream failed (${response.status})`);
      }

      setIsConnected(true);
      logSseDebug("connected", { url: streamUrl, documentIds: activeIds });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffered = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffered += decoder.decode(value, { stream: true });

        const { events, rest } = parseEventBlocks(buffered);
        buffered = rest;

        for (const rawEvent of events) {
          const parsed = parseSseEvent(rawEvent);
          if (!parsed || parsed.event !== "document_status") continue;
          const payload = parseDocumentStatusPayload(parsed.data);
          if (payload) handleStatusEvent(payload);
        }
      }

      if (!controller.signal.aborted && isAuthenticated) {
        reconnectTimerRef.current = window.setTimeout(() => {
          void connectSseRef.current?.();
        }, 10000);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setIsConnected(false);
      logSseDebug("disconnected", err);
      if (isAuthenticated) {
        reconnectTimerRef.current = window.setTimeout(() => {
          void connectSseRef.current?.();
        }, 10000);
      }
    }
  }, [disconnectSse, handleStatusEvent, isAuthenticated]);

  connectSseRef.current = connectSse;

  const bootstrapJobs = useCallback(
    async (initial: Record<string, DocumentExtractionJob>) => {
      const ids = Object.values(initial)
        .filter((j) => {
          if (!isTerminalEtlStatus(j.status)) return true;
          // Re-verify jobs marked complete against API (may actually be in_review).
          return isEtlComplete(j.status);
        })
        .map((j) => j.documentId);
      if (ids.length === 0) return;

      setIsBootstrapping(true);
      const concurrency = 4;
      for (let i = 0; i < ids.length; i += concurrency) {
        const batch = ids.slice(i, i + concurrency);
        await Promise.all(
          batch.map(async (documentId) => {
            try {
              const result = await apolloClient.query<{
                document?: {
                  id: string;
                  etl?: {
                    status?: string | null;
                    progress?: number | null;
                    stage?: string | null;
                    error?: string | null;
                    processed_at?: string | null;
                  } | null;
                } | null;
              }>({
                query: GET_DOCUMENT_QUERY,
                variables: { id: documentId },
                fetchPolicy: "network-only",
              });

              const doc = result.data?.document;
              if (!doc?.etl) return;

              const fetchedAt = new Date().toISOString();
              const backendProgress =
                doc.etl?.progress != null ? Number(doc.etl.progress) : null;

              setJobs((prev) => {
                const job = prev[documentId];
                if (
                  !job ||
                  !shouldApplyBootstrap(
                    job,
                    fetchedAt,
                    backendProgress,
                    doc.etl?.status
                  )
                ) {
                  return prev;
                }

                const statusRaw = doc.etl?.status ?? "processing";
                const existingProgress = job.progress ?? 0;
                const normalizedStatus = normalizeStreamStatus(statusRaw);
                const isFinished =
                  normalizedStatus === "complete" ||
                  normalizedStatus === "in_review";
                const resolvedProgress = isFinished
                  ? 100
                  : Math.max(backendProgress ?? 0, existingProgress);

                const payload: StatusEventPayload = {
                  documentId,
                  status: normalizedStatus,
                  progress: resolvedProgress,
                  stage: doc.etl?.stage ?? job.stage,
                  error: doc.etl?.error ?? null,
                  processed_at: doc.etl?.processed_at ?? fetchedAt,
                };
                const merged = applyStatusEvent(job, payload, { fromBootstrap: true });
                return persistJobs({ ...prev, [documentId]: merged });
              });
            } catch {
              // skip failed bootstrap for single doc
            }
          })
        );
      }
      setIsBootstrapping(false);
    },
    [apolloClient, persistJobs]
  );

  const scheduleBootstrap = useCallback(
    (documentIds: string[]) => {
      const activeIds = documentIds.filter((id) => {
        const job = jobsRef.current[id];
        return job && !isTerminalEtlStatus(job.status);
      });
      if (activeIds.length === 0) return;

      if (bootstrapTimerRef.current) clearTimeout(bootstrapTimerRef.current);
      bootstrapTimerRef.current = window.setTimeout(() => {
        const subset: Record<string, DocumentExtractionJob> = {};
        for (const id of activeIds) {
          const job = jobsRef.current[id];
          if (job && !isTerminalEtlStatus(job.status)) {
            subset[id] = job;
          }
        }
        if (Object.keys(subset).length > 0) {
          void bootstrapJobs(subset);
        }
      }, 200);
    },
    [bootstrapJobs]
  );

  const scheduleSseReconnect = useCallback(() => {
    if (!isAuthenticated) return;
    const activeKey = getActiveJobIds(jobsRef.current).sort().join(",");
    if (activeKey === activeSseIdsRef.current) return;

    if (sseReconnectTimerRef.current) clearTimeout(sseReconnectTimerRef.current);
    sseReconnectTimerRef.current = window.setTimeout(() => {
      void connectSseRef.current?.();
    }, 300);
  }, [isAuthenticated]);

  // Auth: restore + bootstrap + SSE on login; teardown on logout
  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSse();
      clearAllJobs();
      sessionInitializedRef.current = false;
      activeSseIdsRef.current = "";
      return;
    }

    let cancelled = false;

    const startSession = async () => {
      if (!sessionInitializedRef.current) {
        sessionInitializedRef.current = true;
        const restored = loadJobsFromStorage();
        if (Object.keys(restored).length > 0) {
          setJobs(restored);
          await bootstrapJobs(restored);
        }
      }
      if (!cancelled) {
        void connectSse();
      }
    };

    void startSession();

    return () => {
      cancelled = true;
      disconnectSse();
    };
  }, [isAuthenticated, bootstrapJobs, connectSse, clearAllJobs, disconnectSse]);

  // Reconnect SSE when tracked active document set changes
  const activeJobIdsKey = useMemo(
    () => getActiveJobIds(jobs).sort().join(","),
    [jobs]
  );

  useEffect(() => {
    if (!isAuthenticated || !sessionInitializedRef.current) return;
    scheduleSseReconnect();
  }, [activeJobIdsKey, isAuthenticated, scheduleSseReconnect]);

  // Clear localStorage when no active processing jobs remain
  useEffect(() => {
    if (!isAuthenticated) return;
    if (countActiveJobs(jobs) === 0 && Object.keys(jobs).length > 0) {
      clearJobsStorage();
    }
  }, [jobs, isAuthenticated]);

  // Flush jobs to storage before unload
  useEffect(() => {
    const onPageHide = () => flushJobsToStorage();
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [flushJobsToStorage]);

  // Re-evaluate stuck-in-queue cards every 60s
  useEffect(() => {
    const id = window.setInterval(() => {
      setStuckCheckTick((t) => t + 1);
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // Cross-tab token removal
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accessToken" && !e.newValue) {
        disconnectSse();
        clearAllJobs();
        sessionInitializedRef.current = false;
        activeSseIdsRef.current = "";
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [clearAllJobs, disconnectSse]);

  // Hard-deleted documents should disappear from the uploads bell immediately.
  useEffect(() => {
    return onDocumentDeleted((documentId) => {
      autoOpenedReviewRef.current.delete(documentId);
      setReviewReadyJob((prev) =>
        prev?.documentId === documentId ? null : prev
      );
      setJobs((prev) => {
        const next = removeJobsByDocumentIds(prev, documentId);
        return next === prev ? prev : persistJobs(next);
      });
    });
  }, [persistJobs]);

  const registerJobs = useCallback(
    (docs: RegisterDocInput[], context: DocumentUploadContext) => {
      if (docs.length === 0) return;
      const now = new Date().toISOString();
      const label = buildDefaultContextLabel(context);
      const registeredIds: string[] = [];

      setJobs((prev) => {
        const next = { ...prev };
        for (const doc of docs) {
          const existing = next[doc.id];
          if (existing && doc.status && shouldSyncJobFromApi(existing, doc.status)) {
            const incomingStatus = normalizeStreamStatus(doc.status);
            const nextProgress = resolveJobProgress(existing.progress, doc.progress);
            next[doc.id] = {
              ...existing,
              status: incomingStatus,
              progress: nextProgress,
              stage: doc.stage ?? existing.stage,
              updatedAt: now,
            };
            if (!isTerminalEtlStatus(incomingStatus)) {
              registeredIds.push(doc.id);
            }
            continue;
          }
          if (existing && isJobActive(existing) && !isJobStuckInQueue(existing)) {
            const nextStatus = doc.status
              ? normalizeStreamStatus(doc.status)
              : existing.status;
            const nextProgress = resolveJobProgress(existing.progress, doc.progress);
            next[doc.id] = {
              ...existing,
              status: nextStatus,
              progress: nextProgress,
              stage: doc.stage ?? existing.stage,
              updatedAt: now,
            };
            if (!isTerminalEtlStatus(nextStatus)) {
              registeredIds.push(doc.id);
            }
            continue;
          }
          // Create new, or replace failed / stuck / terminal jobs (needed for retry).
          const incomingStatus = doc.status
            ? normalizeStreamStatus(doc.status)
            : "pending";
          const incomingProgress =
            doc.progress != null ? Math.round(Number(doc.progress)) : 0;
          next[doc.id] = {
            documentId: doc.id,
            name: doc.name,
            mime_type: doc.mime_type,
            uploadedAt: doc.queuedSince ?? now,
            status: incomingStatus,
            progress: incomingProgress,
            stage: doc.stage ?? "queued",
            error: null,
            updatedAt: now,
            contextType: context.type,
            contextId: context.id,
            contextLabel: label,
            read: false,
          };
          if (!isTerminalEtlStatus(incomingStatus)) {
            registeredIds.push(doc.id);
          }
        }
        return persistJobs(next);
      });

      if (registeredIds.length > 0) {
        scheduleBootstrap(registeredIds);
        scheduleSseReconnect();
      }

      logSseDebug("registerJobs", { count: docs.length, context });
    },
    [persistJobs, scheduleBootstrap, scheduleSseReconnect]
  );

  const syncJobsFromApi = useCallback(
    (docs: RegisterDocInput[]) => {
      if (docs.length === 0) return;
      const now = new Date().toISOString();

      setJobs((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const doc of docs) {
          if (!doc.status) continue;
          const existing = next[doc.id];
          if (!existing || !shouldSyncJobFromApi(existing, doc.status)) continue;
          const incomingStatus = normalizeStreamStatus(doc.status);
          next[doc.id] = {
            ...existing,
            status: incomingStatus,
            progress: resolveJobProgress(existing.progress, doc.progress),
            stage: doc.stage ?? existing.stage,
            updatedAt: now,
          };
          changed = true;
        }
        return changed ? persistJobs(next) : prev;
      });
    },
    [persistJobs]
  );

  const getJob = useCallback(
    (documentId: string) => jobs[documentId],
    [jobs]
  );

  const dismissJob = useCallback(
    (documentId: string) => {
      autoOpenedReviewRef.current.delete(documentId);
      setReviewReadyJob((prev) =>
        prev?.documentId === documentId ? null : prev
      );
      setJobs((prev) => {
        const next = removeJobsByDocumentIds(prev, documentId);
        return next === prev ? prev : persistJobs(next);
      });
    },
    [persistJobs]
  );

  const markJobRead = useCallback(
    (documentId: string) => {
      setJobs((prev) => {
        const job = prev[documentId];
        if (!job) return prev;
        return persistJobs({ ...prev, [documentId]: { ...job, read: true } });
      });
    },
    [persistJobs]
  );

  const markAllRead = useCallback(() => {
    setJobs((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        if (isJobInReview(next[id]) || isJobCompleted(next[id]) || isJobFailed(next[id])) {
          next[id] = { ...next[id], read: true };
        }
      }
      return persistJobs(next);
    });
  }, [persistJobs]);

  const markJobSubmitted = useCallback(
    (documentId: string) => {
      setJobs((prev) => {
        const job = prev[documentId];
        if (!job) return prev;
        return persistJobs({
          ...prev,
          [documentId]: {
            ...job,
            status: "complete",
            progress: 100,
            read: true,
            updatedAt: new Date().toISOString(),
          },
        });
      });
    },
    [persistJobs]
  );

  const consumeReviewReadyJob = useCallback(() => {
    setReviewReadyJob(null);
  }, []);

  const statusMap = useMemo(() => jobsToStatusMap(jobs), [jobs]);
  const activeCount = useMemo(() => countActiveJobs(jobs), [jobs]);
  const unreadCount = useMemo(() => countUnreadJobs(jobs), [jobs]);
  const badgeCount = useMemo(() => countBadgeJobs(jobs), [jobs]);

  const value = useMemo(
    () => ({
      jobs,
      statusMap,
      isConnected,
      isBootstrapping,
      activeCount,
      unreadCount,
      badgeCount,
      stuckCheckTick,
      registerJobs,
      syncJobsFromApi,
      getJob,
      dismissJob,
      markAllRead,
      markJobRead,
      markJobSubmitted,
      reviewReadyJob,
      consumeReviewReadyJob,
    }),
    [
      jobs,
      statusMap,
      isConnected,
      isBootstrapping,
      activeCount,
      unreadCount,
      badgeCount,
      stuckCheckTick,
      registerJobs,
      syncJobsFromApi,
      getJob,
      dismissJob,
      markAllRead,
      markJobRead,
      markJobSubmitted,
      reviewReadyJob,
      consumeReviewReadyJob,
    ]
  );

  return (
    <DocumentExtractionContext.Provider value={value}>
      {children}
    </DocumentExtractionContext.Provider>
  );
}

export function useDocumentExtraction(): DocumentExtractionContextValue {
  const ctx = useContext(DocumentExtractionContext);
  if (!ctx) {
    throw new Error("useDocumentExtraction must be used within DocumentExtractionProvider");
  }
  return ctx;
}

/** Safe optional hook for components that may render outside provider (should not happen in app shell). */
export function useDocumentExtractionOptional(): DocumentExtractionContextValue | null {
  return useContext(DocumentExtractionContext) ?? null;
}
